// =============================================================================
// themes/editorial — motyw 1 (v2, 2026-09-22): nowoczesny blog redakcyjny.
// Sticky header z jedna linia menu (na mobile przewijana), hero z najnowszym
// wpisem + lista obok, siatka 3 kolumn, sekcja Tematy jako karty, artykul
// w szerokosci czytelniczej z duzym zdjeciem, powiazane jako karty, ciemna
// stopka z kolumnami. Wszystkie klasy z prefiksem z wariacji (footprint).
// =============================================================================

import type { Theme, ThemeRenderers } from "./index";
import type { BrandModel } from "../types";
import type { Variation } from "./variation";
import {
  esc, escAttr, renderHead, renderNav, renderFooter, wrapDocument, postCard, heroCard, postList,
  categoryGrid, renderBreadcrumbs, formatDate, isoDate, imageUrl, readingTime, sortByDateDesc,
  excerptFromHtml, isPost, categoryFor, categoryChip, authorBox, authorAvatar,
} from "./shared";
import { encodePath } from "../urlPolicy";
import { renderNavCss } from "../menuBuilder";

function css(v: Variation, brand: BrandModel | null): string {
  const p = v.classPrefix;
  const t = v.neutralTint;
  const H = v.hue;
  const primary = `hsl(${H} 62% 42%)`;
  const primaryDark = `hsl(${H} 60% 30%)`;
  const primarySoft = `hsl(${H} 70% 95%)`;
  const accent = `hsl(${v.accentHue} 65% 48%)`;
  const bg = `hsl(${H} ${Math.round(t * 25)}% 99%)`;
  const surface = `hsl(${H} ${Math.round(t * 25)}% 96.5%)`;
  const text = `hsl(${H} ${Math.round(t * 25)}% 11%)`;
  const muted = `hsl(${H} ${Math.round(t * 15)}% 40%)`;
  const border = `hsl(${H} ${Math.round(t * 20)}% 89%)`;
  const dark = `hsl(${H} ${Math.round(18 + t * 20)}% 12%)`;
  const darkText = `hsl(${H} 15% 85%)`;
  const r = v.radius;
  const rBig = Math.max(r, 8) + 6;
  void brand;
  return `:root{--${p}primary:${primary};--${p}primary-dark:${primaryDark};--${p}primary-soft:${primarySoft};--${p}accent:${accent};--${p}bg:${bg};--${p}surface:${surface};--${p}text:${text};--${p}muted:${muted};--${p}border:${border};--${p}dark:${dark};--${p}dark-text:${darkText};--${p}r:${r}px;--${p}r-big:${rBig}px;--${p}w:${v.maxWidth + 280}px;--${p}w-read:${Math.min(v.maxWidth, 760)}px}
*,*::before,*::after{box-sizing:border-box}
html{font-size:${Math.round(16 * v.fontScale)}px;-webkit-text-size-adjust:100%;scroll-behavior:smooth}
body{margin:0;font-family:${v.fontStack.body};line-height:1.65;color:var(--${p}text);background:var(--${p}bg)}
img{max-width:100%;height:auto;display:block}
a{color:var(--${p}primary);text-decoration:none}a:hover{color:var(--${p}primary-dark)}
h1,h2,h3,h4{font-family:${v.fontStack.heading};line-height:1.18;margin:0 0 .5em;letter-spacing:-.01em;font-weight:700}
h1{font-size:clamp(1.9rem,4vw,2.7rem)}h2{font-size:clamp(1.35rem,2.6vw,1.7rem)}h3{font-size:1.15rem}
.${p}wrap{max-width:var(--${p}w);margin:0 auto;padding:0 1.25rem}
.${p}eyebrow{display:inline-block;font-size:.72rem;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:var(--${p}accent);margin-bottom:.4rem}
.${p}chip{display:inline-block;font-size:.72rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--${p}primary);background:var(--${p}primary-soft);padding:.25rem .6rem;border-radius:999px;margin-bottom:.6rem}
.${p}chip:hover{background:var(--${p}primary);color:#fff}
.${p}btn{display:inline-block;background:var(--${p}primary);color:#fff;padding:.7rem 1.3rem;border-radius:var(--${p}r);font-weight:600;font-size:.95rem;margin-top:1rem}
.${p}btn:hover{background:var(--${p}primary-dark);color:#fff}
.${p}link-more{font-weight:600;font-size:.9rem}

/* header */
.${p}header{position:sticky;top:0;z-index:20;background:rgba(255,255,255,.92);backdrop-filter:blur(8px);border-bottom:1px solid var(--${p}border)}
.${p}header-in{display:flex;align-items:center;gap:2rem;min-height:64px}
.${p}nav-centered .${p}header-in{flex-direction:column;gap:.4rem;padding-top:.7rem;padding-bottom:.6rem}
.${p}nav-split .${p}header-in{justify-content:space-between}
.${p}logo{display:inline-flex;align-items:center;gap:.6rem;font-family:${v.fontStack.heading};font-weight:800;font-size:1.2rem;color:var(--${p}text);white-space:nowrap;letter-spacing:-.02em}
.${p}logo img{width:36px;height:36px}
.${p}logo-mark{display:inline-flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:9px;background:var(--${p}primary);color:#fff;font-size:1rem}
${renderNavCss(p, { text: `var(--${p}text)`, muted: `var(--${p}muted)`, bg: `var(--${p}bg)`, surface: `var(--${p}surface)`, border: `var(--${p}border)`, accent: `var(--${p}accent)`, radius: `var(--${p}r)`, headerHeight: "64px", breakpoint: 900 })}
.${p}nav-split .${p}nav{flex:0 1 auto}
.${p}nav-split .${p}nav-list,.${p}nav-centered .${p}nav-list{justify-content:flex-end}
.${p}nav-centered .${p}nav-list{justify-content:center}
.${p}fnav{display:contents}.${p}fnav-col h4{margin:0 0 .6rem;font-size:.8rem;letter-spacing:.1em;text-transform:uppercase}

/* home */
.${p}main{padding:2.2rem 0 4rem}
.${p}intro{padding:1.2rem 0 2rem}
.${p}intro h1{margin:0 0 .35rem}
.${p}intro p{margin:0;color:var(--${p}muted);font-size:1.1rem;max-width:62ch}
.${p}hero{display:grid;grid-template-columns:minmax(0,1.55fr) minmax(0,1fr);gap:2rem;align-items:start;margin-bottom:3rem}
.${p}hero-card{display:flex;flex-direction:column;background:#fff;border:1px solid var(--${p}border);border-radius:var(--${p}r-big);overflow:hidden}
.${p}hero-img img{width:100%;aspect-ratio:16/9;object-fit:cover}
.${p}hero-body{padding:1.5rem 1.7rem 1.8rem}
.${p}hero-body h2{font-size:clamp(1.5rem,2.8vw,2rem);margin-bottom:.6rem}
.${p}hero-body h2 a{color:var(--${p}text)}.${p}hero-body h2 a:hover{color:var(--${p}primary)}
.${p}hero-body p{color:var(--${p}muted);margin:0 0 .6rem;font-size:1.02rem}
.${p}aside h3{font-size:.78rem;text-transform:uppercase;letter-spacing:.12em;color:var(--${p}muted);margin:0 0 .9rem;padding-bottom:.6rem;border-bottom:2px solid var(--${p}border)}
.${p}list{list-style:none;margin:0;padding:0;display:grid;gap:.9rem}
.${p}list li{display:flex;gap:.9rem;align-items:flex-start}
.${p}list-img{flex:0 0 84px}.${p}list-img img{width:84px;height:64px;object-fit:cover;border-radius:var(--${p}r)}
.${p}list-title{display:block;font-weight:600;line-height:1.3;color:var(--${p}text);font-family:${v.fontStack.heading}}
.${p}list-title:hover{color:var(--${p}primary)}
.${p}list time{display:block;font-size:.8rem;color:var(--${p}muted);margin-top:.2rem}
.${p}section{margin:3rem 0}
.${p}section-head{display:flex;align-items:baseline;justify-content:space-between;gap:1rem;margin-bottom:1.2rem;padding-bottom:.6rem;border-bottom:2px solid var(--${p}border)}
.${p}section-head h2{margin:0;font-size:1.3rem}
.${p}section-head a{font-size:.9rem;font-weight:600}
.${p}grid{display:grid;gap:1.4rem;grid-template-columns:repeat(auto-fill,minmax(280px,1fr))}
.${p}card{background:#fff;border:1px solid var(--${p}border);border-radius:var(--${p}r-big);overflow:hidden;display:flex;flex-direction:column;transition:transform .15s,box-shadow .15s}
.${p}card:hover{transform:translateY(-2px);box-shadow:0 10px 30px -12px hsl(${H} 40% 30% / .25)}
.${p}card-c{flex-direction:row}
.${p}card-c .${p}card-img{flex:0 0 38%}.${p}card-c .${p}card-img img{height:100%;aspect-ratio:auto}
.${p}card-img img{width:100%;aspect-ratio:16/10;object-fit:cover}
.${p}card-img-empty{display:flex;align-items:center;justify-content:center;aspect-ratio:16/10;background:linear-gradient(135deg,var(--${p}primary),var(--${p}accent));color:#fff;font-family:${v.fontStack.heading};font-size:2.2rem;font-weight:800}
.${p}card-body{padding:1.1rem 1.2rem 1.2rem;display:flex;flex-direction:column;flex:1}
.${p}card h3{font-size:1.12rem;margin:0 0 .45rem}
.${p}card h3 a{color:var(--${p}text)}.${p}card h3 a:hover{color:var(--${p}primary)}
.${p}card p{margin:0 0 .8rem;color:var(--${p}muted);font-size:.93rem;flex:1;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.${p}card-meta{display:flex;flex-wrap:wrap;gap:.35rem .9rem;font-size:.8rem;color:var(--${p}muted)}
.${p}card-meta span+span::before,.${p}card-meta time+span::before,.${p}card-meta span+time::before{content:"·";margin-right:.9rem;opacity:.6}
.${p}cat-grid{display:grid;gap:.9rem;grid-template-columns:repeat(auto-fill,minmax(210px,1fr))}
.${p}cat-card{display:flex;flex-direction:column;gap:.25rem;padding:1rem 1.1rem;background:#fff;border:1px solid var(--${p}border);border-radius:var(--${p}r-big);color:var(--${p}text)}
.${p}cat-card:hover{border-color:var(--${p}primary);color:var(--${p}text)}
.${p}cat-name{font-weight:700;font-family:${v.fontStack.heading}}
.${p}cat-desc{font-size:.82rem;color:var(--${p}muted);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.${p}cat-count{font-size:.75rem;color:var(--${p}primary);font-weight:600;margin-top:.2rem}

/* author */
.${p}avatar{border-radius:50%;object-fit:cover;flex:0 0 auto}
.${p}avatar-sm{width:36px;height:36px}.${p}avatar-lg{width:88px;height:88px}
.${p}avatar-txt{display:inline-flex;align-items:center;justify-content:center;background:var(--${p}primary);color:#fff;font-weight:700;font-family:${v.fontStack.heading}}
.${p}avatar-sm.${p}avatar-txt{font-size:.85rem}.${p}avatar-lg.${p}avatar-txt{font-size:1.8rem}
.${p}author-box{display:flex;gap:1.3rem;align-items:flex-start;padding:1.5rem;background:var(--${p}surface);border-radius:var(--${p}r-big)}
.${p}author-box h3{margin:0 0 .2rem;font-size:1.2rem}
.${p}author-box h3 a{color:var(--${p}text)}
.${p}author-box p{margin:0 0 .5rem;color:var(--${p}muted);font-size:.95rem}
.${p}author-role{font-weight:600;color:var(--${p}primary)!important;font-size:.85rem!important}

/* article */
.${p}read{max-width:var(--${p}w-read);margin:0 auto}
.${p}crumbs ol{list-style:none;padding:0;margin:0 0 1.2rem;display:flex;flex-wrap:wrap;gap:.4rem;font-size:.82rem;color:var(--${p}muted)}
.${p}crumbs li+li::before{content:"›";margin-right:.4rem;opacity:.6}
.${p}crumbs a{color:var(--${p}muted)}.${p}crumbs a:hover{color:var(--${p}primary)}
.${p}crumbs li[aria-current]{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:40ch}
.${p}article-head{margin-bottom:1.6rem}
.${p}article-head h1{margin:.4rem 0 1rem}
.${p}lead{font-size:1.15rem;color:var(--${p}muted);margin:0 0 1.2rem;line-height:1.55}
.${p}meta{display:flex;align-items:center;flex-wrap:wrap;gap:.6rem 1rem;color:var(--${p}muted);font-size:.88rem}
.${p}meta-author{display:inline-flex;align-items:center;gap:.6rem;font-weight:600;color:var(--${p}text)}
.${p}featured{margin:0 0 2.2rem;border-radius:var(--${p}r-big);overflow:hidden}
.${p}featured img{width:100%;aspect-ratio:16/9;object-fit:cover}
.${p}content{font-size:1.08rem;line-height:1.75}
.${p}content>*:first-child{margin-top:0}
.${p}content h2{margin:2.4em 0 .7em;font-size:1.55rem;padding-top:.3em;position:relative}
.${p}content h2::before{content:"";position:absolute;top:0;left:0;width:40px;height:3px;background:var(--${p}accent);border-radius:2px}
.${p}content h3{margin:1.8em 0 .5em;font-size:1.22rem}
.${p}content p{margin:0 0 1.2em}
.${p}content a{text-decoration:underline;text-decoration-color:hsl(${H} 60% 70%);text-underline-offset:3px}
.${p}content a:hover{text-decoration-color:var(--${p}primary)}
.${p}content img{border-radius:var(--${p}r);margin:1.6rem auto}
.${p}content ul,.${p}content ol{padding-left:1.4rem;margin:0 0 1.2em}
.${p}content li{margin:.3em 0}
.${p}content table{border-collapse:collapse;width:100%;margin:1.6rem 0;font-size:.95rem;display:block;overflow-x:auto}
.${p}content th,.${p}content td{border:1px solid var(--${p}border);padding:.6rem .8rem;text-align:left;vertical-align:top}
.${p}content th{background:var(--${p}surface);font-weight:600}
.${p}content blockquote{margin:1.6rem 0;padding:.9rem 1.3rem;border-left:4px solid var(--${p}accent);background:var(--${p}surface);border-radius:0 var(--${p}r) var(--${p}r) 0;font-size:1.05rem}
.${p}content blockquote p:last-child{margin:0}
.${p}content figure{margin:1.6rem 0}
.${p}content figcaption{font-size:.85rem;color:var(--${p}muted);text-align:center;margin-top:.5rem}
.${p}content pre{overflow-x:auto;background:var(--${p}dark);color:var(--${p}dark-text);padding:1rem 1.2rem;border-radius:var(--${p}r);font-size:.9rem}
.${p}content code{font-size:.92em}
.${p}content hr{border:0;border-top:1px solid var(--${p}border);margin:2rem 0}
.${p}article-foot{margin-top:3rem;display:grid;gap:2.5rem}
.${p}related h2{font-size:1.3rem;margin-bottom:1.2rem}

/* archive */
.${p}archive-head{margin-bottom:2rem;padding-bottom:1.4rem;border-bottom:1px solid var(--${p}border)}
.${p}archive-head p{color:var(--${p}muted);margin:.4rem 0 0;max-width:70ch;font-size:1.05rem}
.${p}archive-count{font-size:.85rem;color:var(--${p}muted)}

/* author page */
.${p}author-hero{display:flex;gap:2rem;align-items:center;margin-bottom:2.2rem;padding:2rem;background:var(--${p}surface);border-radius:var(--${p}r-big)}
.${p}author-hero .${p}avatar-lg{width:140px;height:140px}
.${p}author-hero .${p}avatar-lg.${p}avatar-txt{font-size:3rem}
.${p}author-hero h1{margin:0 0 .3rem}
.${p}author-hero p{margin:0;color:var(--${p}muted)}

/* 404 */
.${p}notfound{text-align:center;padding:4rem 0 2rem}
.${p}notfound h1{font-size:5rem;margin:0;color:var(--${p}primary);line-height:1}
.${p}notfound p{color:var(--${p}muted);font-size:1.1rem}

/* footer */
.${p}footer{background:var(--${p}dark);color:var(--${p}dark-text);margin-top:3rem}
.${p}footer a{color:var(--${p}dark-text)}.${p}footer a:hover{color:#fff}
.${p}footer-grid{display:grid;gap:2.5rem;grid-template-columns:1.4fr 1fr 1fr;padding-top:3rem;padding-bottom:2.5rem}
.${p}footer .${p}logo{color:#fff;margin-bottom:.6rem}
.${p}footer-brand p{margin:0;font-size:.95rem;opacity:.8;max-width:38ch}
.${p}footer h4{margin:0 0 .8rem;font-size:.78rem;text-transform:uppercase;letter-spacing:.12em;color:#fff;opacity:.9}
.${p}footer ul{list-style:none;margin:0;padding:0;display:grid;gap:.45rem;font-size:.92rem}
.${p}footer-bottom{border-top:1px solid hsl(${H} 15% 25%);padding-top:1.2rem;padding-bottom:1.4rem;font-size:.85rem;opacity:.7}

@media (max-width:900px){
.${p}hero{grid-template-columns:1fr}
.${p}footer-grid{grid-template-columns:1fr 1fr}
.${p}nav-centered .${p}header-in{align-items:stretch}
}
@media (max-width:640px){
.${p}header-in{gap:1rem;min-height:56px}
.${p}logo{font-size:1.05rem}
.${p}main{padding-top:1.4rem}
.${p}intro{padding:.6rem 0 1.4rem}
.${p}hero-body{padding:1.2rem 1.2rem 1.4rem}
.${p}card-c{flex-direction:column}.${p}card-c .${p}card-img{flex:none}.${p}card-c .${p}card-img img{aspect-ratio:16/10}
.${p}content{font-size:1.02rem}
.${p}author-box{flex-direction:column;align-items:flex-start;padding:1.2rem}
.${p}author-hero{flex-direction:column;text-align:center;padding:1.4rem}
.${p}footer-grid{grid-template-columns:1fr;gap:1.8rem;padding-top:2.2rem}
.${p}crumbs li[aria-current]{max-width:22ch}
}
`;
}

const renderers: ThemeRenderers = {
  home(ctx) {
    const { site, v } = ctx;
    const p = v.classPrefix;
    const posts = sortByDateDesc(site.pages.filter(isPost));
    const [hero, ...rest] = posts;
    const aside = rest.slice(0, 5);
    const latest = rest.slice(5, 5 + v.postsPerPage);
    const pillars = site.pages.filter((pg) => pg.kind === "pillar");
    const cats = [...site.categories].sort((a, b) => b.count - a.count);

    const blocks: Record<string, string> = {
      hero: hero
        ? `<section class="${p}hero">
${heroCard(ctx, hero)}
<aside class="${p}aside"><h3>Warto przeczytać</h3>${postList(ctx, aside)}</aside>
</section>`
        : "",
      latest: latest.length
        ? `<section class="${p}section"><div class="${p}section-head"><h2>Najnowsze artykuły</h2>${cats[0] ? `<a href="${escAttr(encodePath(cats[0].path))}">${esc(cats[0].name)} →</a>` : ""}</div><div class="${p}grid">${latest.map((pg) => postCard(ctx, pg, "a")).join("\n")}</div></section>`
        : "",
      categories: cats.length
        ? `<section class="${p}section" id="tematy"><div class="${p}section-head"><h2>Tematy</h2><span class="${p}archive-count">${cats.length} ${cats.length === 1 ? "kategoria" : cats.length < 5 ? "kategorie" : "kategorii"}</span></div>${categoryGrid(ctx, cats)}${pillars.length ? `<div class="${p}section-head" style="margin-top:2rem"><h2>Przewodniki</h2></div><div class="${p}grid">${pillars.slice(0, 6).map((pl) => postCard(ctx, pl, "a")).join("\n")}</div>` : ""}</section>`
        : "",
      about: site.author ? `<section class="${p}section">${authorBox(ctx, site.author, { heading: true })}</section>` : "",
    };
    const body = `${renderNav(ctx)}
<main class="${p}main"><div class="${p}wrap">
<div class="${p}intro"><h1>${esc(site.siteName)}</h1>${site.settings.tagline ? `<p>${esc(site.settings.tagline)}</p>` : ""}</div>
${v.blockOrder.map((b) => blocks[b]).filter(Boolean).join("\n")}
</div></main>
${renderFooter(ctx)}`;
    const head = renderHead(ctx, {
      title: site.settings.tagline ? `${site.siteName} — ${site.settings.tagline}` : site.siteName,
      description: site.settings.tagline || null,
      canonicalPath: "/",
      ogType: "website",
      ogImage: hero?.featuredImageUrl ? imageUrl(ctx, hero.featuredImageUrl) : null,
      jsonLd: null,
    });
    return wrapDocument(ctx, head, body, `${p}home`);
  },

  article(ctx, page) {
    const { site, v } = ctx;
    const p = v.classPrefix;
    const cat = categoryFor(site, page);
    const crumbs = renderBreadcrumbs(ctx, [
      { name: "Start", path: "/" },
      ...(cat ? [{ name: cat.name, path: cat.path }] : []),
      { name: page.title, path: null },
    ]);
    const date = formatDate(page.datePublished, v.dateFormat, site.language);
    const mod = page.dateModified && page.dateModified.slice(0, 10) !== (page.datePublished || "").slice(0, 10) ? formatDate(page.dateModified, v.dateFormat, site.language) : "";
    const featured = imageUrl(ctx, page.featuredImageUrl);
    const lead = page.excerpt || page.metaDescription || "";
    const sameCat = sortByDateDesc(site.pages.filter((pg) => pg.id !== page.id && isPost(pg) && cat && pg.categorySlug === cat.slug));
    const related = (sameCat.length >= 3 ? sameCat : sortByDateDesc(site.pages.filter((pg) => pg.id !== page.id && isPost(pg)))).slice(0, 3);
    const author = site.author;
    const body = `${renderNav(ctx, cat?.path)}
<main class="${p}main"><div class="${p}wrap"><div class="${p}read">
${crumbs}
<article>
<header class="${p}article-head">
${categoryChip(ctx, page)}
<h1>${esc(page.title)}</h1>
${lead ? `<p class="${p}lead">${esc(lead)}</p>` : ""}
<div class="${p}meta">
${author ? `<span class="${p}meta-author">${authorAvatar(ctx, author, "sm")}${author.path ? `<a href="${escAttr(encodePath(author.path))}">${esc(author.name)}</a>` : esc(author.name)}</span>` : ""}
${date ? `<time datetime="${escAttr(isoDate(page.datePublished))}">${esc(date)}</time>` : ""}
${mod ? `<span>aktualizacja ${esc(mod)}</span>` : ""}
<span>${readingTime(page.bodyHtml)} min czytania</span>
</div>
</header>
${featured ? `<figure class="${p}featured"><img src="${escAttr(featured)}" alt="${escAttr(page.title)}" fetchpriority="high"></figure>` : ""}
<div class="${p}content">
${page.bodyHtml}
</div>
</article>
<div class="${p}article-foot">
${author ? authorBox(ctx, author, { heading: true }) : ""}
${related.length ? `<section class="${p}related"><h2>Czytaj dalej</h2><div class="${p}grid">${related.map((r) => postCard(ctx, r, "a")).join("\n")}</div></section>` : ""}
</div>
</div></div></main>
${renderFooter(ctx)}`;
    const head = renderHead(ctx, {
      title: page.seoTitle || page.title,
      description: page.metaDescription || page.excerpt || excerptFromHtml(page.bodyHtml),
      canonicalPath: page.path,
      ogType: "article",
      ogImage: featured || null,
      jsonLd: page.schemaGraph,
      published: page.datePublished,
      modified: page.dateModified,
    });
    return wrapDocument(ctx, head, body, `${p}single`);
  },

  category(ctx, category, pages) {
    const { site, v } = ctx;
    const p = v.classPrefix;
    const sorted = sortByDateDesc(pages);
    const body = `${renderNav(ctx, category.path)}
<main class="${p}main"><div class="${p}wrap">
${renderBreadcrumbs(ctx, [{ name: "Start", path: "/" }, { name: category.name, path: null }])}
<header class="${p}archive-head"><span class="${p}eyebrow">Temat</span><h1>${esc(category.name)}</h1>${category.description ? `<p>${esc(category.description)}</p>` : ""}<span class="${p}archive-count">${sorted.length} ${sorted.length === 1 ? "artykuł" : sorted.length < 5 ? "artykuły" : "artykułów"}</span></header>
<div class="${p}grid">${sorted.map((pg) => postCard(ctx, pg, "a")).join("\n")}</div>
</div></main>
${renderFooter(ctx)}`;
    const head = renderHead(ctx, {
      title: `${category.name} — ${site.siteName}`,
      description: category.description || `Artykuły w kategorii ${category.name}`,
      canonicalPath: category.path,
      ogType: "website",
      ogImage: null,
      jsonLd: null,
    });
    return wrapDocument(ctx, head, body, `${p}archive`);
  },

  author(ctx, page) {
    const { site, v } = ctx;
    const p = v.classPrefix;
    const a = site.author;
    const posts = sortByDateDesc(site.pages.filter(isPost)).slice(0, 9);
    const body = `${renderNav(ctx, page.path)}
<main class="${p}main ${p}author-page"><div class="${p}wrap">
${renderBreadcrumbs(ctx, [{ name: "Start", path: "/" }, { name: page.title, path: null }])}
<section class="${p}author-hero">${a ? authorAvatar(ctx, a, "lg") : ""}<div><span class="${p}eyebrow">Autor</span><h1>${esc(page.title)}</h1>${a?.jobTitle ? `<p>${esc(a.jobTitle)}</p>` : ""}</div></section>
<div class="${p}read"><div class="${p}content">
${page.bodyHtml}
</div></div>
${posts.length ? `<section class="${p}section"><div class="${p}section-head"><h2>Artykuły autora</h2></div><div class="${p}grid">${posts.map((r) => postCard(ctx, r, "a")).join("\n")}</div></section>` : ""}
</div></main>
${renderFooter(ctx)}`;
    const head = renderHead(ctx, {
      title: page.seoTitle || `${page.title} — ${site.siteName}`,
      description: page.metaDescription || excerptFromHtml(page.bodyHtml),
      canonicalPath: page.path,
      ogType: "website",
      ogImage: a?.photoUrl ? imageUrl(ctx, a.photoUrl) : null,
      jsonLd: page.schemaGraph,
    });
    return wrapDocument(ctx, head, body, `${p}author`);
  },

  page(ctx, page) {
    const { site, v } = ctx;
    const p = v.classPrefix;
    const body = `${renderNav(ctx, page.path)}
<main class="${p}main"><div class="${p}wrap"><div class="${p}read">
${renderBreadcrumbs(ctx, [{ name: "Start", path: "/" }, { name: page.title, path: null }])}
<article>
<header class="${p}article-head"><h1>${esc(page.title)}</h1></header>
<div class="${p}content">
${page.bodyHtml}
</div>
</article>
</div></div></main>
${renderFooter(ctx)}`;
    const head = renderHead(ctx, {
      title: page.seoTitle || `${page.title} — ${site.siteName}`,
      description: page.metaDescription || excerptFromHtml(page.bodyHtml),
      canonicalPath: page.path,
      ogType: "website",
      ogImage: page.featuredImageUrl ? imageUrl(ctx, page.featuredImageUrl) : null,
      jsonLd: page.schemaGraph,
    });
    return wrapDocument(ctx, head, body, `${p}page`);
  },

  notFound(ctx) {
    const { site, v } = ctx;
    const p = v.classPrefix;
    const cats = [...site.categories].sort((a, b) => b.count - a.count).slice(0, 8);
    const body = `${renderNav(ctx)}
<main class="${p}main"><div class="${p}wrap">
<div class="${p}notfound">
<h1>404</h1>
<p>Nie znaleźliśmy takiej strony.</p>
<a class="${p}btn" href="/">Wróć na stronę główną</a>
</div>
${cats.length ? `<section class="${p}section"><div class="${p}section-head"><h2>Może szukasz</h2></div>${categoryGrid(ctx, cats)}</section>` : ""}
</div></main>
${renderFooter(ctx)}`;
    const head = renderHead(ctx, {
      title: `Nie znaleziono — ${site.siteName}`,
      description: null,
      canonicalPath: "/404/",
      ogType: "website",
      ogImage: null,
      jsonLd: null,
      noindex: true,
    });
    return wrapDocument(ctx, head, body, `${p}notfound-page`);
  },
};

export const editorialTheme: Theme = {
  id: "editorial",
  label: "Editorial",
  description: "Nowoczesny blog redakcyjny: hero z najnowszym wpisem, siatka kart, tematy, ciemna stopka.",
  css,
  renderers,
};
